import { useMemo, useState } from 'react';
import { View, StyleSheet } from 'react-native';

import { Spacing } from '@/constants/spacing';
import { RouteLegCard } from '@/components/route-planner/RouteLegCard';
import type { TransportMode } from '@/types/transport';
import type { FamilyMember } from '@/types/family-member';
import type { RouteLeg, RoutePlanStop } from '@/types/route-plan';

type RouteReviewTimelineProps = {
  stops: RoutePlanStop[];
  legs: RouteLeg[];
  familyMembers: FamilyMember[];
  onSetDuration: (legId: string, minutes: number) => void;
  onChangeMode: (legId: string, mode: TransportMode) => void;
  onDeleteLeg: (legId: string) => void;
};

/**
 * RouteReviewTimeline — wizard step 3 body: the alternating stay/travel
 * timeline. Tapping a travel leg expands its transport-mode selector; all
 * edits re-flow times locally (no AI round trip).
 */
export function RouteReviewTimeline({
  stops,
  legs,
  familyMembers,
  onSetDuration,
  onChangeMode,
  onDeleteLeg,
}: RouteReviewTimelineProps) {
  const [expandedLegId, setExpandedLegId] = useState<string | null>(null);

  const stopsById = useMemo(() => new Map(stops.map((stop) => [stop.id, stop])), [stops]);

  const inlineTodoTitles = (leg: RouteLeg): string[] => {
    if (leg.kind !== 'travel') return [];
    return (leg.inlineTodoStopIds ?? [])
      .map((stopId) => stopsById.get(stopId)?.title)
      .filter((title): title is string => Boolean(title));
  };

  return (
    <View style={styles.container}>
      {legs.map((leg) => (
        <RouteLegCard
          key={leg.id}
          leg={leg}
          stop={leg.kind === 'stay' ? stopsById.get(leg.stopId) : undefined}
          inlineTodoTitles={inlineTodoTitles(leg)}
          familyMembers={familyMembers}
          expanded={expandedLegId === leg.id}
          onPressTravel={(legId) =>
            setExpandedLegId((current) => (current === legId ? null : legId))
          }
          onSetDuration={onSetDuration}
          onChangeMode={onChangeMode}
          onDeleteLeg={onDeleteLeg}
        />
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: Spacing.xs,
  },
});
